import { useEffect, useRef } from "react"

import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"

import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import {
  designSaveRecalculatingState,
  syncWaitingState,
  syncWaitingForEntityState,
  brochureSyncEpochState,
} from "~/containers/Proposal/state/sync-state"
import type { AllBrochureFieldsFragment } from "~/graphql/client"
import { useGetBrochureQuery } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"

import { brochureState } from "./brochure-state"

export type UseBrochure = (proposalId: string) => {
  brochure: AllBrochureFieldsFragment
  refetch: ReturnType<typeof useGetBrochureQuery>[`refetch`]
}

export const useBrochure: UseBrochure = (proposalId: string) => {
  const [brochure, setBrochure] = useRecoilState(brochureState)
  const shouldSyncBrochure = useRecoilValue(
    syncWaitingForEntityState(`brochure`),
  )
  const brochureSyncEpoch = useRecoilValue(brochureSyncEpochState)
  const lastFetchedBrochureSyncEpoch = useRef(0)
  const setSyncWaiting = useSetRecoilState(syncWaitingState)
  const setDesignSaveRecalculating = useSetRecoilState(
    designSaveRecalculatingState,
  )
  const lastBrochureUpdatedAt = useRef(brochure.updatedAt)
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const { refetch } = useGetBrochureQuery(
    client,
    { proposalId },
    {
      enabled: !!proposalId,
      staleTime: Infinity,
      onError,
      onSuccess: ({ getBrochure: newBrochure }) => {
        setSyncWaiting((prev) => ({ ...prev, brochure: false }))
        if (newBrochure) {
          try {
            setBrochure(newBrochure)
            console.count(`useBrochure`)
            // Only clear recalculating state when brochure data
            // has actually changed (new updatedAt from server)
            if (newBrochure.updatedAt !== lastBrochureUpdatedAt.current) {
              lastBrochureUpdatedAt.current = newBrochure.updatedAt
              setDesignSaveRecalculating(false)
            }
          } catch (error) {
            console.log(error)
            onError(error)
          }
        }

        return brochure
      },
    },
  )

  useEffect(() => {
    const epochChanged =
      brochureSyncEpoch !== lastFetchedBrochureSyncEpoch.current
    lastFetchedBrochureSyncEpoch.current = brochureSyncEpoch

    if (shouldSyncBrochure || (epochChanged && brochureSyncEpoch > 0)) {
      void refetch()
    }
  }, [refetch, shouldSyncBrochure, brochureSyncEpoch])

  return { brochure, refetch }
}
